import React from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@adsk/alloy-react-tooltip';
import { useLocale } from '@adsk/alloy-react-helpers';
import SelectBox from '@adsk/alloy-select-box';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import { AlertWarningIcon } from '@adsk/alloy-react-icon';
import {
  availableTextStyle,
  dropdownStyle,
  dropdownLabelStyle,
  dropdownLabelNameStyle,
  dropdownLabelSheetStyle,
  dropdownValueStyle,
  dropdownOptionStyle,
  optionNameStyle,
  iconStyle,
  sheetCountErrorStyle,
} from './styles';
import { ACCOUNT_OFFERING_TYPES, NO_LICENSE_PLC } from '../../constants';

const OfferingDropdown = ({
  category,
  external,
  label,
  options,
  onChange,
  saving,
  userSheetCount,
  value,
}) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));

  const isOptionDisabled = ({ quantity }) =>
    quantity && quantity.available === 0;

  const addNoLicenseOption = () => {
    return [
      ...options,
      {
        name: T.translate('MEMBER_SUBSCRIPTION_DROPDOWN_NO_LICENSE_OPTION'),
        plc: NO_LICENSE_PLC,
        category,
        categoryName: label,
        licensingModel: null,
      },
    ];
  };

  const optionRenderer = (license) => {
    const isDisabled = license.quantity && license.quantity.available === 0;
    const isTokenFlex =
      license.type === 'token-flex' ||
      license.type === ACCOUNT_OFFERING_TYPES.TOKEN_FLEX;

    const commercialLicenseText =
      license.quantity &&
      `${license.quantity.available} ${T.translate(
        'MEMBER_SUBSCRIPTION_AVAILABLE_TEXT'
      )}`;

    const tokenflexLicenseText = T.translate(
      'MEMBER_SUBSCRIPTION_DROPDOWN_TOKEN_FLEX'
    );

    return (
      <div css={dropdownOptionStyle}>
        <div css={optionNameStyle(isDisabled)}>{license.name}</div>
        <div css={availableTextStyle(isDisabled)}>
          {isTokenFlex ? tokenflexLicenseText : commercialLicenseText}
        </div>
      </div>
    );
  };

  const renderDropdown = () => (
    <SelectBox
      disabled={external}
      isOptionDisabled={isOptionDisabled}
      labelKey="name"
      onChange={onChange}
      options={addNoLicenseOption()}
      optionRenderer={optionRenderer}
      placeholder={
        external
          ? T.translate('MEMBER_SUBSCRIPTION_DROPDOWN_EXTERNAL_TEXT')
          : T.translate('MEMBER_SUBSCRIPTION_DROPDOWN_PLACEHOLDER')
      }
      value={value}
      valueRenderer={(val) => {
        return (
          <div css={dropdownValueStyle}>
            <div>{val.name}</div>
            {saving && <ProgressRing size={ProgressRing.SIZES.XSMALL} />}
          </div>
        );
      }}
      valueKey="plc"
    />
  );

  const showSheetCount =
    typeof userSheetCount === 'number' &&
    userSheetCount >= 0 &&
    value !== null &&
    value.plc !== NO_LICENSE_PLC;

  const showSheetCountError =
    userSheetCount === -1 && value !== null && value.plc !== NO_LICENSE_PLC;

  return (
    <div css={dropdownStyle}>
      <div css={dropdownLabelStyle}>
        <div css={dropdownLabelNameStyle}>{label}</div>
        {!external && showSheetCount && (
          <div css={dropdownLabelSheetStyle}>
            {T.translate('MEMBER_SUBSCRIPTION_DROPDOWN_SHEET_TEXT', {
              userSheetCount,
            })}
          </div>
        )}
        {showSheetCountError && (
          <div css={sheetCountErrorStyle}>
            <AlertWarningIcon style={iconStyle} />
            {T.translate('MEMBER_SUBSCRIPTION_DROPDOWN_SHEET_TEXT_ERROR')}
          </div>
        )}
      </div>
      {external ? (
        <Tooltip
          content={T.translate(
            'MEMBER_SUBSCRIPTION_DROPDOWN_EXTERNAL_TOOLTIP_TEXT'
          )}
          placement={Tooltip.PLACEMENTS.BOTTOM_END}
        >
          {renderDropdown()}
        </Tooltip>
      ) : (
        <div>{renderDropdown()}</div>
      )}
    </div>
  );
};

OfferingDropdown.propTypes = {
  category: PropTypes.string,
  external: PropTypes.bool,
  label: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.object),
  saving: PropTypes.bool.isRequired,
  userSheetCount: PropTypes.number,
  value: PropTypes.object,
};

OfferingDropdown.defaultProps = {
  category: '',
  external: false,
  label: '',
  options: [],
  userSheetCount: null,
  value: false,
};

export default OfferingDropdown;
